import { Deal } from 'models/deals'
import { DealReport } from 'models/deals_reports'
import { SalesStage } from 'models/sales_stages'
import { User } from 'models/user'
import {
  formatInteger,
  formatUsd,
  getDaysSinceColorClass,
} from 'utils/formatting'
import { IconType } from 'react-icons'
import {
  PiArrowLeft,
  PiArrowRight,
  PiArrowsClockwise,
  PiCheck,
  PiConfetti,
  PiCopy,
  PiEye,
  PiHandshake,
  PiHourglass,
  PiPackage,
  PiProhibit,
  PiProhibitInset,
  PiTrash,
  PiUser,
  PiX,
} from 'react-icons/pi'
import dialogConfirm from 'components/dialogConfirm'
import { usePreferences } from 'context/preferences/PreferencesContext'
import useApiService from 'services/api'
import { toast } from 'react-toastify'
import {
  getStageColor,
  getStageKind,
  sortStagesPipelineOrder,
  StageKind,
} from 'components/SalesStage/stageColors'
import {
  ActionsDropdown,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import { DropdownMenuItem } from 'components/UI/BaseDropdown/dropdown-base'
import { StatusBadge } from 'components/FbUI/StatusBadge'
import { cn } from 'components/UI/cn'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useCompanyUrl, useCompanyV2Url } from 'utils/useCompanyUrl'
import { ContactCompanyType } from 'models/contact_companies'

export type DealOrReport = Deal | DealReport

function isDealReport(deal: DealOrReport): deal is DealReport {
  return 'cc_doors_cnt' in deal || 'sales_stage_id' in deal
}

/**
 * Stage-kind → icon for the colored footer band. Mirrors SalesStageBar so the
 * company-page card and the deal-detail bar read as the same system. Buckets
 * are gone, so every pipeline stage shares the handshake; terminals keep their
 * semantic icons.
 */
const STAGE_KIND_ICONS: Record<StageKind, IconType> = {
  pipeline: PiHandshake,
  // Closed Won earns a little celebration in the footer band.
  won: PiConfetti,
  lost: PiProhibit,
  churn: PiProhibitInset,
}

/**
 * Freshness level → DS2026 subtle badge pill (bg + text). Keyed by the solid
 * `text-*` token `getDaysSinceColorClass` returns so the day thresholds stay
 * single-sourced in formatting.ts rather than duplicated here.
 */
const FRESHNESS_PILL_CLASS: Record<string, string> = {
  'text-success': 'bg-success-bg-subtle text-success-strong',
  'text-warning': 'bg-warning-bg-subtle text-warning-strong',
  'text-error': 'bg-error-bg-subtle text-error-strong',
}

interface DealCardProps<T extends DealOrReport> {
  deal: T
  salesStages?: SalesStage[]
  users?: User[]
  onEdit?: (deal: T) => void
  /** Show the deal's company name (linked) under the deal title. Off on the
   *  company page (redundant there); on for the all-deals kanban board. */
  showCompanyName?: boolean
  /**
   * Optional override for the "Change stage" menu. When provided (the Kanban
   * board passes this), selecting a stage delegates to the parent instead of
   * the card's built-in mutation. The board needs to own the move so it can
   * reconcile its infinite per-column caches optimistically WITHOUT an
   * immediate refetch — refetching the board's CDC read-model the instant the
   * PATCH resolves races propagation and snaps the card back ~half the time
   * (the same reason drag-and-drop is parent-owned). Omit it elsewhere (e.g.
   * the company page) to keep the card's self-contained mutation.
   */
  onStageChange?: (deal: T, toStageId: number) => void
  /**
   * Whether tapping the card / the "View deal" action may open the deal detail
   * page. Defaults to true (desktop). Mobile passes false: the deal detail page
   * isn't routed on mobile, so navigating there would 404 — the card stays
   * non-interactive and drops "View deal", while edit / change-stage / delete
   * stay available in the kebab. (Still also gated by the `dealDetailPage` flag.)
   */
  canViewDetail?: boolean
  /**
   * Whether to render the colored stage banner at the bottom of the card.
   * Defaults to true everywhere the card carries its own stage identity
   * (company page, mobile list, etc.). The Kanban board passes false: there the
   * stage is identified by the colored column header, so a per-card banner is
   * redundant and just steals vertical space from the cards.
   */
  showStageFooter?: boolean
}

export function DealCard<T extends DealOrReport>({
  deal,
  salesStages,
  users,
  onEdit,
  showCompanyName = false,
  onStageChange,
  canViewDetail = true,
  showStageFooter = true,
}: DealCardProps<T>) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const getCompanyUrl = useCompanyUrl()
  const getCompanyV2Url = useCompanyV2Url()
  const { dealDetailPage } = useFeatureFlagService()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  // Normalize field access for both Deal and DealReport types
  const accountOwnerId = isDealReport(deal)
    ? deal.account_owner_id
    : deal.account_owner
  const salesStageId = isDealReport(deal)
    ? deal.sales_stage_id
    : deal.sales_stage
  const totalDoors = isDealReport(deal)
    ? deal.cc_doors_cnt
    : deal.contact_company?.doors_cnt
  const dealName = isDealReport(deal) ? deal.deal_name : deal.name
  const isNonRecurring = deal.is_not_recurring

  // Company name + link to its detail page. DealReport carries flat `cc_*`
  // fields; Deal nests them under `contact_company`. Resolve a URL via the
  // matching helper; `'-'` means the prefix map isn't loaded yet, so we
  // fall back to plain text instead of a dead link.
  const companyName = isDealReport(deal)
    ? deal.cc_company_name
    : deal.contact_company?.name
  const rawCompanyUrl = isDealReport(deal)
    ? getCompanyV2Url({
        company_type: deal.cc_company_type,
        chain_id: deal.cc_chain_id,
        university_id: deal.cc_university_id,
        k12district_id: deal.cc_k12district_id,
        lodging_id: deal.cc_lodging_id,
        id: deal.cc_id,
      })
    : deal.contact_company
      ? getCompanyUrl({
          id: deal.contact_company.id,
          company_type: deal.contact_company.company_type as ContactCompanyType,
          chain: String(deal.contact_company.chain),
          university_id: deal.contact_company.university_id ?? undefined,
          k12district_id: deal.contact_company.k12district_id ?? undefined,
          lodging_id: deal.contact_company.lodging_id ?? undefined,
        })
      : '-'
  const companyUrl =
    rawCompanyUrl && rawCompanyUrl !== '-' ? rawCompanyUrl : null

  // Find the account owner
  const accountOwner = users?.find((u) => u.id === accountOwnerId)
  const accountOwnerName = accountOwner
    ? `${accountOwner.first_name} ${accountOwner.last_name}`
    : 'No Owner'

  // Calculate days since last modified
  const daysSinceModified = deal.days_since_deal_last_modified || 0
  const freshnessPillClass =
    FRESHNESS_PILL_CLASS[getDaysSinceColorClass(daysSinceModified, 'text')] ??
    'bg-success-bg-subtle text-success-strong'

  // Door count data
  const selectedDoors = deal.doors_count || totalDoors
  const doorCountData = {
    selectedDoors: formatInteger(selectedDoors),
    totalDoors: formatInteger(totalDoors),
    locationText: totalDoors !== 1 ? 'locations' : 'location',
  }

  // Revenue: annual when present, else predicted monthly for recurring products.
  const hasRevenue =
    deal.annual_revenue != null ||
    (!isNonRecurring && deal.monthly_revenue_predicted != null)
  const revenueValue =
    deal.annual_revenue ??
    (!isNonRecurring && deal.monthly_revenue_predicted != null
      ? deal.monthly_revenue_predicted
      : 0)

  const updateStageMutation = useMutation({
    // `null` clears the deal back to "no stage" (mirrors the deal-detail page's
    // onClearStage path); a number moves it to that stage.
    mutationFn: async (newStageId: number | null) => {
      return api.editDeal(deal.id, { sales_stage: newStageId })
    },
    onMutate: async (newStageId: number | null) => {
      await queryClient.cancelQueries({ queryKey: ['deals'] })
      await queryClient.cancelQueries({ queryKey: ['report-deals'] })

      const previousQueries: any[] = []
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['deals'] })
        .forEach((query) => {
          previousQueries.push({
            queryKey: query.queryKey,
            data: query.state.data,
          })
        })
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['report-deals'] })
        .forEach((query) => {
          previousQueries.push({
            queryKey: query.queryKey,
            data: query.state.data,
          })
        })

      queryClient.setQueriesData({ queryKey: ['deals'] }, (old: any) => {
        if (!old?.results) return old
        return {
          ...old,
          results: old.results.map((d: Deal) =>
            d.id === deal.id ? { ...d, sales_stage: newStageId } : d
          ),
        }
      })
      queryClient.setQueriesData({ queryKey: ['report-deals'] }, (old: any) => {
        if (!old?.results) return old
        return {
          ...old,
          results: old.results.map((d: DealReport) =>
            d.id === deal.id ? { ...d, sales_stage_id: newStageId } : d
          ),
        }
      })

      return { previousQueries }
    },
    onError: (_err, _newStageId, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      toast.error('Failed to update deal stage. Please try again.')
    },
    onSuccess: (_data, newStageId) => {
      if (newStageId === null) {
        toast.success('Stage cleared')
        return
      }
      const newStage = salesStages?.find((s) => s.id === newStageId)
      toast.success(
        `Deal moved to ${newStage?.stage || 'new stage'} successfully!`
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['report-deals'] })
    },
  })

  const handleStageChange = (newStageId: number | null) => {
    // Same-stage selection is a no-op (and the menu marks it with a check).
    if (newStageId === salesStageId) return
    // When the parent owns stage changes (the Kanban board), delegate real stage
    // moves so it can reconcile its per-column caches without a CDC-racing
    // refetch. Clearing (null) isn't part of that contract, so it always runs
    // the card's self-contained mutation, which invalidates both deal caches.
    if (onStageChange && newStageId !== null) {
      onStageChange(deal, newStageId)
      return
    }
    updateStageMutation.mutate(newStageId)
  }

  const deleteMutation = useMutation({
    mutationFn: async () => api.deleteDeal(deal.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['report-deals'] })
      toast.success('Deal deleted')
    },
    onError: () => {
      toast.error('Failed to delete deal')
    },
  })

  const handleDelete = async () => {
    const confirmed = await dialogConfirm(
      "Are you sure you want to delete this deal? This can't be undone.",
      'Delete deal',
      '',
      { confirmLabel: 'Delete', buttonVariant: 'danger' }
    )
    if (confirmed) deleteMutation.mutate()
  }

  // The deal detail page is desktop-only; `canViewDetail` is false on mobile
  // (no /deals/:id route there) so the card neither navigates nor offers "View
  // deal" — it'd 404. Still also gated by the dealDetailPage feature flag.
  const canOpenDetail = dealDetailPage && canViewDetail

  const handleCardClick = () => {
    if (canOpenDetail) {
      navigate(`/deals/${deal.id}`)
    }
  }

  const handleCopyId = (e: React.MouseEvent) => {
    e.stopPropagation()
    void navigator.clipboard?.writeText(`#${deal.identifier}`)
    toast.success('Deal ID copied')
  }

  // Current stage drives the footer band color/label.
  const currentStage = salesStages?.find((s) => s.id === salesStageId) ?? null
  const tone =
    currentStage && salesStages
      ? getStageColor(currentStage, salesStages, customOrderEnabled)
      : null
  const FooterIcon = currentStage
    ? STAGE_KIND_ICONS[getStageKind(currentStage)]
    : null

  // Stage list for the "Change stage" submenu — pipeline stages first (in
  // order), then the closed outcomes under their own header. Each stage shows
  // its own color dot; no phase buckets.
  const sortedStages = salesStages
    ? sortStagesPipelineOrder(salesStages, customOrderEnabled)
    : []
  const pipelineStages = sortedStages.filter(
    (s) => getStageKind(s) === 'pipeline'
  )
  const outcomeStages = sortedStages.filter(
    (s) => getStageKind(s) !== 'pipeline'
  )
  // Quick one-step moves through the pipeline sequence. If the deal is at a
  // terminal/no stage, "forward" re-enters at the first pipeline stage.
  const currentPipelineIdx = pipelineStages.findIndex(
    (s) => s.id === salesStageId
  )
  const nextStage =
    currentPipelineIdx === -1
      ? (pipelineStages[0] ?? null)
      : (pipelineStages[currentPipelineIdx + 1] ?? null)
  const prevStage =
    currentPipelineIdx > 0 ? pipelineStages[currentPipelineIdx - 1] : null

  const stageItemClass =
    '!flex !cursor-pointer !items-center !gap-2.5 !rounded-lg !px-3 !py-2 !text-sm !font-medium !tracking-[-0.2px] !text-neutral-700 hover:!bg-neutral-100 hover:!text-neutral-900 focus:!bg-neutral-100 focus:!text-neutral-900 data-[disabled]:!cursor-not-allowed data-[disabled]:!opacity-40'

  const renderStageItem = (stage: SalesStage) => {
    const dot = getStageColor(stage, salesStages!, customOrderEnabled).dot
    const isCurrent = stage.id === salesStageId
    return (
      <DropdownMenuItem
        key={stage.id}
        onSelect={() => handleStageChange(stage.id)}
        className={stageItemClass}
      >
        <span className={cn('size-2 flex-shrink-0 rounded-full', dot)} />
        <span className="flex-1 truncate">{stage.stage}</span>
        {isCurrent && (
          <PiCheck size={16} className="flex-shrink-0 text-brand-active" />
        )}
      </DropdownMenuItem>
    )
  }
  const stageMenu = (
    <div className="max-h-72 overflow-y-auto">
      <DropdownMenuItem
        disabled={!nextStage}
        onSelect={() => nextStage && handleStageChange(nextStage.id)}
        className={stageItemClass}
      >
        <PiArrowRight size={16} className="flex-shrink-0" />
        <span className="flex-1">Move forward</span>
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={!prevStage}
        onSelect={() => prevStage && handleStageChange(prevStage.id)}
        className={stageItemClass}
      >
        <PiArrowLeft size={16} className="flex-shrink-0" />
        <span className="flex-1">Move backward</span>
      </DropdownMenuItem>
      <div className="my-1 border-t border-line" />
      {pipelineStages.map(renderStageItem)}
      {outcomeStages.length > 0 && (
        <>
          <p className="mt-1 border-t border-line px-2 pb-0.5 pt-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Outcomes
          </p>
          {outcomeStages.map(renderStageItem)}
        </>
      )}
      {salesStageId != null && (
        <>
          <div className="my-1 border-t border-line" />
          <DropdownMenuItem
            onSelect={() => handleStageChange(null)}
            className={stageItemClass}
          >
            <PiX size={16} className="flex-shrink-0" />
            <span className="flex-1">Clear stage</span>
          </DropdownMenuItem>
        </>
      )}
    </div>
  )

  const actions = [
    // "View deal" routes to the deal detail page — only where it's reachable
    // (flag on + not mobile).
    ...(canOpenDetail
      ? [
          {
            label: 'View deal',
            icon: PiEye,
            onClick: () => navigate(`/deals/${deal.id}`),
          },
        ]
      : []),
    ...(onEdit ? [createEditAction(() => onEdit(deal), 'Edit deal')] : []),
    ...(sortedStages.length > 0
      ? [
          {
            label: 'Change stage',
            icon: PiArrowsClockwise,
            onClick: () => {},
            subContent: stageMenu,
          },
        ]
      : []),
    // ActionsDropdown auto-inserts a divider before the first destructive item.
    {
      label: 'Delete deal',
      icon: PiTrash,
      onClick: handleDelete,
      variant: 'destructive' as const,
    },
  ]

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        // `shrink-0`: the card must keep its natural height. In a height-bounded
        // flex column (the mobile deals list, a board column) it would otherwise
        // be squished as a flex item and its own `overflow-hidden` would clip the
        // body — leaving an empty card with only the clipped header.
        // Hover lift is always on as a card affordance; the pointer cursor only
        // appears when the card can actually open the detail page.
        'flex shrink-0 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-lg',
        canOpenDetail && 'cursor-pointer'
      )}
    >
      {/* Body */}
      <div className="flex flex-col gap-2 p-4">
        {/* Identifier + name + actions */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-col">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium tabular-nums tracking-[-0.2px] text-neutral-400">
                #{deal.identifier}
              </span>
              <button
                type="button"
                onClick={handleCopyId}
                aria-label="Copy deal ID"
                className="text-neutral-400 transition-colors hover:text-neutral-600"
              >
                <PiCopy size={14} />
              </button>
            </div>
            <h3 className="mt-0.5 line-clamp-2 text-lg font-semibold leading-7 tracking-[-0.2px] text-neutral-800">
              {deal.deal_display_name || dealName || 'Untitled Deal'}
            </h3>
            {showCompanyName &&
              companyName &&
              (companyUrl ? (
                <Link
                  to={companyUrl}
                  onClick={(e) => e.stopPropagation()}
                  // DS2026 text-link style: color shift on hover, no underline.
                  className="block truncate text-sm font-medium tracking-[-0.2px] text-brand-bright transition-colors hover:text-brand-active"
                >
                  {companyName}
                </Link>
              ) : (
                <span className="block truncate text-sm font-medium tracking-[-0.2px] text-neutral-500">
                  {companyName}
                </span>
              ))}
          </div>
          {actions.length > 0 && (
            // Stop the click from bubbling to the card root's onClick (which
            // navigates to the detail page). Radix portals the menu, but React
            // portals still propagate events up the React tree, so an item
            // click (e.g. "Edit deal") would otherwise trigger handleCardClick.
            <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
              <ActionsDropdown
                actions={actions}
                orientation="horizontal"
                // Roomier hover/open background than the default icon-tight w-5
                // trigger — card-scoped via triggerClassName, so other kebabs
                // app-wide are unaffected. `!` beats the base w-5/h-5/rounded.
                triggerClassName="!h-7 !w-7 !rounded-md"
              />
            </div>
          )}
        </div>

        {/* Days since last update — freshness-tinted icon pill (playground style) */}
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'flex size-6 flex-shrink-0 items-center justify-center rounded-full',
              freshnessPillClass
            )}
          >
            <PiHourglass size={12} />
          </span>
          <span className="text-sm tabular-nums tracking-[-0.2px] text-neutral-500">
            {daysSinceModified} days since last update
          </span>
        </div>

        {/* Revenue + locations */}
        <div className="flex items-center gap-2">
          {hasRevenue && (
            <>
              <span className="whitespace-nowrap text-sm font-medium tabular-nums tracking-[-0.2px] text-neutral-700">
                {formatUsd(revenueValue)}
              </span>
              <span className="size-1 flex-shrink-0 rounded-full bg-neutral-400" />
            </>
          )}
          <span className="min-w-0 flex-1 truncate text-sm tabular-nums tracking-[-0.2px] text-neutral-500">
            {doorCountData.selectedDoors} / {doorCountData.totalDoors}{' '}
            {doorCountData.locationText}
          </span>
        </div>

        {/* Product + account owner — DS2026 neutral badges, each with a
            leading phosphor icon (box = product, user = owner). */}
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <StatusBadge color="neutral" icon={PiPackage} className="min-w-0">
            <span className="truncate">
              {deal.product_name || 'Unknown Product'}
            </span>
          </StatusBadge>
          <StatusBadge color="neutral" icon={PiUser} className="min-w-0">
            <span className="truncate">{accountOwnerName}</span>
          </StatusBadge>
        </div>
      </div>

      {/* Footer band — color encodes pipeline PHASE; text carries the
          individual stage identity. Suppressed on the Kanban board
          (showStageFooter=false), where the colored column header carries the
          stage instead. */}
      {showStageFooter && (
        <div
          className={cn(
            'flex items-center gap-2 px-4 py-2.5',
            tone ? cn(tone.bg, tone.text) : 'bg-gray-100 text-gray-500'
          )}
        >
          {FooterIcon && <FooterIcon size={18} className="flex-shrink-0" />}
          <span className="truncate text-sm font-medium tracking-[-0.2px]">
            {currentStage?.stage ?? 'No stage'}
          </span>
        </div>
      )}
    </div>
  )
}
